/**
 * HospitalOrderRecipients
 * -----------------------
 * Permanent (standing) notification recipients for a hospital's orders.
 * Any order for this hospital that has no per-order recipient list falls back
 * to this one, so order / fulfillment emails always reach the right people.
 *
 * GET  /api/admin/health-systems/{id}/order-recipients
 * PUT  /api/admin/health-systems/{id}/order-recipients
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailCheck, Plus, Trash2, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HospitalOrderRecipients({ healthSystemId, token, suggestions = [] }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [serverSuggestions, setServerSuggestions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/admin/health-systems/${healthSystemId}/order-recipients`,
        { headers },
      );
      const saved = r.data?.recipients || [];
      const suggested = r.data?.suggested || [];
      // Only pre-fill the single best suggestion (the hospital's primary
      // contact). Everything else is offered as a one-tap chip so we never
      // silently build a 10-address distribution list.
      const list = saved.length > 0 ? saved : suggested.slice(0, 1);
      setServerSuggestions(suggested.slice(1));
      setRows(list.length > 0 ? list : [{ name: "", email: "", role: "primary" }]);
      setDirty(saved.length === 0 && list.length > 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load order recipients");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthSystemId, token]);
  useEffect(() => { if (healthSystemId) load(); }, [healthSystemId, load]);

  const setRow = (i, patch) => {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const addRow = (seed = { name: "", email: "", role: "cc" }) => {
    setRows((rs) => [...rs, seed]);
    setDirty(true);
  };
  const removeRow = (i) => {
    setRows((rs) => (rs.length === 1 ? [{ name: "", email: "", role: "primary" }] : rs.filter((_, idx) => idx !== i)));
    setDirty(true);
  };
  const makePrimary = (i) => {
    setRows((rs) => rs.map((r, idx) => ({ ...r, role: idx === i ? "primary" : "cc" })));
    setDirty(true);
  };

  const save = async () => {
    const clean = rows
      .map((r) => ({ name: (r.name || "").trim() || null, email: (r.email || "").trim(), role: r.role || "cc" }))
      .filter((r) => r.email);
    const bad = clean.find((r) => !r.email.includes("@"));
    if (bad) { toast.error(`"${bad.email}" is not a valid email address.`); return; }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/api/admin/health-systems/${healthSystemId}/order-recipients`,
        { recipients: clean },
        { headers },
      );
      setRows(r.data?.recipients?.length ? r.data.recipients : [{ name: "", email: "", role: "primary" }]);
      setDirty(false);
      toast.success(
        clean.length === 0
          ? "Standing recipients cleared."
          : `${clean.length} standing recipient${clean.length === 1 ? "" : "s"} saved for this hospital.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save order recipients");
    } finally {
      setSaving(false);
    }
  };

  const unusedSuggestions = [...serverSuggestions, ...(suggestions || [])].filter(
    (s, i, arr) =>
      s.email &&
      !rows.some((r) => (r.email || "").toLowerCase() === s.email.toLowerCase()) &&
      arr.findIndex((x) => (x.email || "").toLowerCase() === s.email.toLowerCase()) === i,
  );

  return (
    <div className="mt-4 pt-4 border-t border-slate-200" data-testid="hospital-order-recipients">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h4 className="font-medium text-slate-700 flex items-center gap-2">
            <MailCheck className="w-4 h-4 text-emerald-600" />
            Order Notification Recipients
          </h4>
          <p className="text-xs text-slate-500 mt-1">
            Standing list for this hospital. Every order email (acknowledgment, shipping notice, delivery)
            uses these addresses unless you set different recipients on that specific order.
            The <strong>To</strong> row is the primary contact; the rest are CC&rsquo;d.
          </p>
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={saving || loading || !dirty}
          className="bg-slate-900 hover:bg-slate-800 shrink-0"
          data-testid="hospital-order-recipients-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          {dirty ? "Save recipients" : "Saved"}
        </Button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`hor-row-${i}`}>
              <Input
                value={r.name || ""}
                onChange={(e) => setRow(i, { name: e.target.value })}
                placeholder="Name (optional)"
                className="col-span-3 h-9 text-sm"
                data-testid={`hor-name-${i}`}
              />
              <Input
                value={r.email || ""}
                onChange={(e) => setRow(i, { email: e.target.value })}
                placeholder="name@hospital.org"
                className="col-span-6 h-9 text-sm"
                data-testid={`hor-email-${i}`}
              />
              <button
                type="button"
                onClick={() => makePrimary(i)}
                className={`col-span-2 h-9 rounded-md text-[11px] font-bold uppercase tracking-wider border transition-colors ${
                  r.role === "primary"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                }`}
                data-testid={`hor-primary-${i}`}
              >
                {r.role === "primary" ? "To" : "CC"}
              </button>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="col-span-1 h-9 text-rose-600 hover:bg-rose-50 rounded inline-flex items-center justify-center"
                data-testid={`hor-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => addRow()} data-testid="hor-add">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add email address
            </Button>
            {unusedSuggestions.slice(0, 6).map((s) => (
              <button
                key={s.email}
                type="button"
                onClick={() => addRow({ name: s.name || "", email: s.email, role: "cc" })}
                className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-700"
                title={`Add ${s.email}`}
                data-testid={`hor-suggest-${s.email}`}
              >
                + {s.name || s.email}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
